import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

interface SelectedSpaceState {
  selectedSpaceId: string | null;
  setSelectedSpaceId: (spaceId: string | null) => void;
}

const SelectedSpaceContext = createContext<SelectedSpaceState | null>(null);

interface SelectedSpaceProviderProps {
  children: ReactNode;
}

// A `/?space=<room_id>` deep link lands here (routing is pathname-based, so an
// external entry point can't set the in-memory selection directly). Read the
// param once on mount to open that space, then strip it so a refresh/back
// doesn't re-pin the selection.
function readSpaceFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const space = new URLSearchParams(window.location.search).get('space');
  return space && space.trim() ? space : null;
}

export function SelectedSpaceProvider({ children }: SelectedSpaceProviderProps) {
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(
    readSpaceFromUrl,
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('space')) return;
    params.delete('space');
    const qs = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
    );
  }, []);

  const value = useMemo(
    () => ({ selectedSpaceId, setSelectedSpaceId }),
    [selectedSpaceId],
  );
  return (
    <SelectedSpaceContext.Provider value={value}>
      {children}
    </SelectedSpaceContext.Provider>
  );
}

export function useSelectedSpace(): SelectedSpaceState {
  const ctx = useContext(SelectedSpaceContext);
  if (!ctx) {
    throw new Error('useSelectedSpace must be used inside <SelectedSpaceProvider>');
  }
  return ctx;
}
